import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, Check, Copy, Loader2, Plus } from 'lucide-react'
import { api } from '../lib/api'
import { formatDate } from '../lib/format'
import { Card, CardBody } from '../components/ui/Card'
import { Chip } from '../components/ui/Badge'
import Button from '../components/ui/Button'
import Modal from '../components/ui/Modal'
import { SearchField, SelectField, TextField } from '../components/ui/Field'
import { PhoneField } from '../components/ui/PhoneField'
import { EmptyState } from '../components/ui/DataTable'
import { SkeletonText } from '../components/ui/Skeleton'
import { useToast } from '../components/ui/Toast'

const MODULES = [
  { value: 'grievance', label: 'Grievance Management' },
  { value: 'feedback', label: 'Employee Feedback & Suggestions' },
  { value: 'survey', label: 'Pulse Surveys' },
  { value: 'posh', label: 'POSH (Internal Complaints Committee)' },
  { value: 'whistleblower', label: 'Whistleblower Mechanism' },
  { value: 'integration', label: 'Data Fabric REST API' },
]

/**
 * Every client organisation this deployment serves.
 *
 * Provisioning a tenant here calls the exact same TenantProvisioner the
 * `tenant:create` console command uses — a tenant created from this screen
 * and one created over SSH are indistinguishable afterwards, right down to
 * how the first administrator's password is generated and shown once.
 *
 * A row opens the full tenant detail page (/tenants/:id) rather than a
 * modal — that page now carries per-module stats, admin counts and
 * suspension history, which is too much for a dialog to hold comfortably.
 */
export default function Tenants() {
  const navigate = useNavigate()
  const toast = useToast()

  const [tenants, setTenants] = useState(null)
  const [search, setSearch] = useState('')
  const [adding, setAdding] = useState(false)
  const [credential, setCredential] = useState(null)

  async function load() {
    try {
      const data = await api.get('/platform/tenants', { params: search ? { search } : undefined })
      setTenants(data ?? [])
    } catch (err) {
      toast.error(err.message)
      setTenants([])
    }
  }

  useEffect(() => {
    const timer = setTimeout(load, search ? 350 : 0)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-semibold text-[var(--color-ink-900)]">
            Tenants
          </h1>
          <p className="mt-0.5 text-sm text-[var(--color-ink-500)]">
            Every client organisation this deployment serves.
          </p>
        </div>

        <Button variant="primary" icon={Plus} onClick={() => setAdding(true)}>
          Provision a tenant
        </Button>
      </div>

      <Card>
        <div className="border-b border-[var(--color-line)] p-3">
          <SearchField
            wrapperClassName="max-w-xs"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name, code or contact email"
            aria-label="Search tenants"
          />
        </div>

        <CardBody padded={false}>
          {tenants === null ? (
            <div className="p-5"><SkeletonText lines={4} /></div>
          ) : tenants.length === 0 ? (
            <EmptyState
              icon={Building2}
              title={search ? 'Nobody matches that' : 'No tenants yet'}
              description={search ? 'Try a different name, code or email.' : 'Provision the first client organisation to get started.'}
            />
          ) : (
            <ul className="divide-y divide-[var(--color-line)]">
              {tenants.map((tenant) => (
                <li
                  key={tenant.id}
                  className="flex cursor-pointer flex-wrap items-center justify-between gap-3 px-5 py-3.5 transition-colors duration-150 hover:bg-[var(--color-surface-sunken)]"
                  onClick={() => navigate(`/tenants/${tenant.id}`)}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    {tenant.logo_url ? (
                      <img src={tenant.logo_url} alt="" className="h-8 w-8 shrink-0 rounded object-contain" />
                    ) : (
                      <span
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-[var(--color-accent-soft)] text-xs font-semibold text-[var(--color-accent-text)]"
                        aria-hidden="true"
                      >
                        {tenant.name?.[0]?.toUpperCase() ?? '?'}
                      </span>
                    )}

                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate font-medium text-[var(--color-ink-900)]">{tenant.name}</span>
                        <span className="font-mono text-xs text-[var(--color-ink-500)]">{tenant.code}</span>
                        {!tenant.is_active && <Chip tone="warning">Suspended</Chip>}
                      </div>
                      <p className="mt-0.5 truncate text-xs text-[var(--color-ink-500)]">
                        {tenant.primary_contact_email ?? 'No contact on file'}
                        {tenant.onboarded_at && ` · onboarded ${formatDate(tenant.onboarded_at)}`}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {adding && (
        <ProvisionDialog
          onClose={() => setAdding(false)}
          onDone={(cred) => { setAdding(false); load(); if (cred) setCredential(cred) }}
          onError={load}
          toast={toast}
        />
      )}

      {credential && (
        <CredentialDialog credential={credential} onClose={() => setCredential(null)} toast={toast} />
      )}
    </div>
  )
}

function ProvisionDialog({ onClose, onDone, onError, toast }) {
  const [form, setForm] = useState({
    code: '', name: '', legal_name: '', timezone: 'Asia/Kolkata', default_locale: 'en',
    modules: [], primary_contact_name: '', primary_contact_email: '', primary_contact_phone: '',
    admin_name: '', admin_email: '', admin_phone: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  function toggleModule(value) {
    setForm((f) => ({
      ...f,
      modules: f.modules.includes(value) ? f.modules.filter((m) => m !== value) : [...f.modules, value],
    }))
  }

  async function submit() {
    setSubmitting(true)
    setError(null)

    try {
      const result = await api.post('/platform/tenants', {
        code: form.code,
        name: form.name,
        legal_name: form.legal_name || undefined,
        timezone: form.timezone || undefined,
        default_locale: form.default_locale || undefined,
        modules: form.modules.length ? form.modules : undefined,
        primary_contact_name: form.primary_contact_name || undefined,
        primary_contact_email: form.primary_contact_email || undefined,
        primary_contact_phone: form.primary_contact_phone || undefined,
        admin_name: form.admin_name,
        admin_email: form.admin_email || undefined,
        admin_phone: form.admin_phone || undefined,
      }, {
        // Provisioning creates a database, runs every tenant migration and
        // seeds reference data — several seconds under good conditions, and
        // the default 30s client timeout is tuned for ordinary JSON calls,
        // not this. A slower disk or a busier dev machine can genuinely push
        // this past 30s; the request should still be allowed to finish
        // rather than being killed and reported as a failure it wasn't.
        timeout: 120000,
      })

      toast.success('Tenant provisioned.')
      onDone(result.admin_password ? { login: result.admin_login, password: result.admin_password } : null)
    } catch (err) {
      // Provisioning is idempotent (TenantProvisioner uses firstOrNew), and
      // the database write can succeed even when the response back to the
      // browser is lost — a slow request racing a dev-server reload, a
      // dropped connection, etc. Refreshing the list in the background means
      // that if it actually went through, it shows up right away instead of
      // only after a manual reload, while a genuine validation failure (e.g.
      // a duplicate code) is harmless to re-fetch against.
      setError(
        err.status === 0
          ? `${err.message} If this was provisioning a tenant, check the list below before retrying — it may have already been created.`
          : err.message,
      )
      onError?.()
    } finally {
      setSubmitting(false)
    }
  }

  const requiredFilled = form.code.trim() && form.name.trim() && form.admin_name.trim()
    && (form.admin_email.trim() || form.admin_phone.trim())

  return (
    <Modal
      open
      onClose={onClose}
      title="Provision a tenant"
      description="Creates the organisation's own database, schema and first administrator."
      size="lg"
      dismissable={!submitting}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={submitting} disabled={!requiredFilled}>
            Provision
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {submitting && (
          <div
            role="status"
            className="flex items-center gap-2.5 rounded-md border px-3 py-2.5 text-sm"
            style={{ borderColor: 'var(--color-accent-soft-border)', backgroundColor: 'var(--color-accent-soft)', color: 'var(--color-accent-text)' }}
          >
            <Loader2 size={16} className="shrink-0 animate-spin" aria-hidden="true" />
            <span>
              Creating the organisation's database and setting up its first administrator —
              this takes several seconds. Please don't close this window.
            </span>
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="rounded-md border px-3 py-2.5 text-sm"
            style={{ borderColor: 'var(--color-danger)', backgroundColor: 'var(--color-danger-bg)', color: 'var(--color-danger)' }}
          >
            {error}
          </div>
        )}

        <div className={submitting ? 'space-y-4 opacity-50 pointer-events-none' : 'space-y-4'} aria-disabled={submitting || undefined}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Code"
              required
              hint="Short slug — used for the database name and channel routing. Cannot be changed later."
              value={form.code}
              onChange={(event) => setForm({ ...form, code: event.target.value.toLowerCase() })}
            />
            <TextField
              label="Organisation name"
              required
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </div>

          <TextField
            label="Legal name"
            hint="Optional. Defaults to the organisation name."
            value={form.legal_name}
            onChange={(event) => setForm({ ...form, legal_name: event.target.value })}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Timezone"
              hint="An IANA zone, e.g. Asia/Kolkata."
              value={form.timezone}
              onChange={(event) => setForm({ ...form, timezone: event.target.value })}
            />
            <SelectField
              label="Default language"
              value={form.default_locale}
              onChange={(event) => setForm({ ...form, default_locale: event.target.value })}
              options={[
                { value: 'en', label: 'English' },
                { value: 'hi', label: 'हिन्दी (Hindi)' },
                { value: 'mr', label: 'मराठी (Marathi)' },
              ]}
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--color-ink-700)]">Modules</label>
            <p className="mb-2 text-xs text-[var(--color-ink-500)]">Leave everything unchecked for every module.</p>
            <div className="flex flex-wrap gap-2">
              {MODULES.map((m) => {
                const checked = form.modules.includes(m.value)
                return (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => toggleModule(m.value)}
                    className="rounded-full border px-3 py-1.5 text-xs font-medium transition-colors duration-150"
                    style={
                      checked
                        ? { borderColor: 'var(--color-accent)', backgroundColor: 'var(--color-accent-soft)', color: 'var(--color-accent-text)' }
                        : { borderColor: 'var(--color-line-strong)', color: 'var(--color-ink-700)' }
                    }
                  >
                    {m.label}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="border-t border-[var(--color-line)] pt-4">
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-[var(--color-ink-500)]">
              Primary contact
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              <TextField
                label="Name"
                value={form.primary_contact_name}
                onChange={(event) => setForm({ ...form, primary_contact_name: event.target.value })}
              />
              <TextField
                label="Email"
                type="email"
                value={form.primary_contact_email}
                onChange={(event) => setForm({ ...form, primary_contact_email: event.target.value })}
              />
              <PhoneField
                label="Phone"
                value={form.primary_contact_phone}
                onChange={(value) => setForm({ ...form, primary_contact_phone: value })}
              />
            </div>
          </div>

          <div className="border-t border-[var(--color-line)] pt-4">
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-[var(--color-ink-500)]">
              First administrator
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              <TextField
                label="Name"
                required
                value={form.admin_name}
                onChange={(event) => setForm({ ...form, admin_name: event.target.value })}
              />
              <TextField
                label="Email"
                type="email"
                hint="Email or phone is required — a login can be created with either."
                value={form.admin_email}
                onChange={(event) => setForm({ ...form, admin_email: event.target.value })}
              />
              <PhoneField
                label="Phone"
                hint="Email or phone is required — a login can be created with either."
                value={form.admin_phone}
                onChange={(value) => setForm({ ...form, admin_phone: value })}
              />
            </div>
            <p className="mt-2 text-xs text-[var(--color-ink-500)]">
              A generated password will be shown once on the next screen.
            </p>
          </div>
        </div>
      </div>
    </Modal>
  )
}

/** Same one-time-reveal pattern as People's login-issuance dialog. */
function CredentialDialog({ credential, onClose, toast }) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(credential.password)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Could not copy — select and copy the password manually.')
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Tenant provisioned"
      size="sm"
      dismissable={false}
      footer={<Button variant="primary" onClick={onClose}>Done</Button>}
    >
      <div className="space-y-4">
        <div
          role="alert"
          className="rounded-md border px-3 py-2.5 text-sm leading-relaxed"
          style={{ borderColor: 'var(--color-warning)', backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-ink-900)' }}
        >
          This is the first administrator's password, shown <strong>once</strong>. Copy it now — it
          cannot be retrieved again, only reset by that admin from within their own portal.
        </div>

        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-ink-500)]">
            {credential.login?.includes('@') ? 'Email' : 'Phone'}
          </p>
          <p className="mt-0.5 font-mono text-sm text-[var(--color-ink-900)]">{credential.login}</p>
        </div>

        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-ink-500)]">Password</p>
          <div className="mt-1 flex items-center gap-2">
            <code className="flex-1 rounded-md border border-[var(--color-line-strong)] bg-[var(--color-surface-sunken)] px-3 py-2 font-mono text-sm text-[var(--color-ink-900)]">
              {credential.password}
            </code>
            <Button size="sm" variant="secondary" icon={copied ? Check : Copy} onClick={copy}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
